import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import DashboardPage from './components/dashboard/DashboardPage';
import MainLayout from './components/layout/MainLayout';
import JobsTable from './components/common/table/JobsTable';
import JobDetail from './features/JobDetail';
import CreateJobDescription from './features/CreateJobDescription';
import GeneratedJobDescription from './features/GeneratedJobDescription';
import Register from './components/auth/Register';
import JobAccessControl from './components/auth/JobAccessControl';
import ConversationalPreScreening from './components/candidateExperience/ConversationalPreScreening';
import PsychoanalyticalTest from './components/candidateExperience/PsychoanalyticalTest';
import TestCompletePage from './components/candidateExperience/common/TestCompletePage';
import UserDashboard from './components/dashboard/UserDashboard';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdminDashboard from './dashboard/AdminDashboard';
import TokenManagement from './dashboard/pages/TokenManagement';
import FinalInterviewTest from './components/candidateExperience/FinalInterviewTest';
import CodingInterviewTest from './components/candidateExperience/CodingInterviewTest';
import StageInterviewTest from './components/candidateExperience/StageInterviewTest';
import StageQuestionnaire from './components/candidateExperience/StageQuestionnaire';
import HumanInterviewPanel from './features/HumanInterviewPanel';
import FinalInterviewPage from './features/HiringStages/FinalInterviewPage';
import Companies from './features/Companies';
import { JobReportsList, InterviewReportDetail } from './features/InterviewReportsPage';
import TalentPool from './pages/TalentPool';
import SettingsPage from './features/settings/SettingsPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  if (!token) {
    return null;
  }

  return <MainLayout>{children}</MainLayout>;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Create a wrapper component to handle params
const JobDetailWrapper = () => {
  const { id } = useParams();
  return (
    <JobAccessControl jobId={id}>
      <JobDetail />
    </JobAccessControl>
  );
};

function App() {
  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/pre-screening-test/:jobId/:candidateId" element={<ConversationalPreScreening />} />
        <Route path="/psychoanalytical-test/:jobId/:candidateId" element={<PsychoanalyticalTest />} />
        <Route path="/test-complete" element={<TestCompletePage />} />
        <Route path="/final-interview/:jobId/:candidateId" element={<FinalInterviewTest />} />
        <Route path="/coding-interview/:jobId/:candidateId" element={<CodingInterviewTest />} />
        {/* Enhancement #1: generic configured-pipeline stage runner */}
        <Route path="/stage-interview/:jobId/:candidateId/:stageId" element={<StageInterviewTest />} />
        {/* Questionnaire — candidate-facing form */}
        <Route path="/stage-questionnaire/:jobId/:candidateId/:stageId" element={<StageQuestionnaire />} />

        {/* Protected Routes */}
        <Route 
          path="/dashboard/*" 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
                    
        {/* User Routes */}
        <Route path="/dashboard/usage" element={
          <ProtectedRoute>
            <UserDashboard />
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin/dashboard" element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          </ProtectedRoute>
        } />
        
        <Route path="/admin/tokens" element={
          <ProtectedRoute>
            <AdminRoute>
              <TokenManagement />
            </AdminRoute>
          </ProtectedRoute>
        } />
        
        <Route path="/admin/final-interview/:id" element={
          <ProtectedRoute>
            <AdminRoute>
              <FinalInterviewPage />
            </AdminRoute>
          </ProtectedRoute>
        } />
        
        {/* HR Interview Panel — full-screen view for human interview */}
        <Route path="/jobs/:jobId/human-interview/:stageId/panel/:resumeId" element={
          <ProtectedRoute>
            <HumanInterviewPanel />
          </ProtectedRoute>
        } />

        {/* Jobs Routes */}
        <Route path="/jobs" element={
          <ProtectedRoute>
            <JobsTable />
          </ProtectedRoute>
        } />
        
        <Route path="/jobs/create" element={
          <ProtectedRoute>
            <CreateJobDescription />
          </ProtectedRoute>
        } />

        <Route path="/jobs/generate" element={
          <ProtectedRoute>
            <GeneratedJobDescription />
          </ProtectedRoute>
        } />

        <Route path="/jobs/:id" element={
          <ProtectedRoute>
            <JobDetailWrapper />
          </ProtectedRoute>
        } />

        {/* Interview reports — comprehensive post-interview reports */}
        <Route path="/jobs/:jobId/reports" element={
          <ProtectedRoute>
            <JobReportsList />
          </ProtectedRoute>
        } />
        <Route path="/jobs/:jobId/reports/:reportId" element={
          <ProtectedRoute>
            <InterviewReportDetail />
          </ProtectedRoute>
        } />

        {/* Companies Routes */}
        <Route path="/companies" element={
          <ProtectedRoute>
            <Companies />
          </ProtectedRoute>
        } />

        {/* Talent Pool */}
        <Route path="/talent-pool" element={
          <ProtectedRoute>
            <TalentPool />
          </ProtectedRoute>
        } />

        {/* Settings Route */}
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
      </Routes>
      <ToastContainer />
    </>
  );
}

export default App;